import { forkJoin, Observable, of, throwError, zip } from 'rxjs'
import {
    concatMap,
    defaultIfEmpty,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    pairwise,
    scan,
    startWith,
    switchMap
} from 'rxjs/operators'
import { ListResponse, Query, RowResponse } from '../responses/response'
import { pushErrorAndEmpty, toastFailedResponse } from '../components/controls/Toaster/Toaster'
import { isFailure, isSuccessArray } from './type-guards'
import { TypedAjaxResponse } from '../http/types'
import { snd } from 'fp-ts/Tuple'

export const paginate = (limit: number) => <T>(source$: Observable<T>): Observable<number> => source$.pipe(
    startWith(),
    scan(acc => acc + limit, -limit)
)

export const paginateSearch = (limit: number) => (source$: Observable<string>): Observable<Query> => zip(
    source$,
    source$.pipe(paginate(limit))
).pipe(
    map(([search, offset]): Query => ({
        limit,
        offset,
        search,
    }))
)


export const listResponse = (defaultError: string) => <T>(source$: Observable<TypedAjaxResponse<ListResponse<T>>>): Observable<T[]> => source$.pipe(
    mergeMap(res => res.response.success ? of(res.response.rows) : pushErrorAndEmpty(res.response, defaultError)),
)

export const rowResponse = (defaultError: string) => <T>(source$: Observable<TypedAjaxResponse<RowResponse<T>>>): Observable<T> => source$.pipe(
    mergeMap(res => res.response.success? of(res.response.row): pushErrorAndEmpty(res.response, defaultError)),
)

export type ResetSearch = { search: string, reset: boolean }

export const fromQueryWithReset = <T>(request: (query: Query) => Observable<TypedAjaxResponse<ListResponse<T>>>) =>
    (source$: Observable<ResetSearch>): Observable<T[]> => source$.pipe(
        startWith({search: '', reset: true}),
        pairwise(),
        filter(([prev, curr]) => prev.search != curr.search || curr.reset),
        map(snd),
        switchMap(({search}) => source$.pipe(
            map(payload => payload.search),
            startWith(search),
            paginateSearch(50),
            concatMap(request),
            listResponse('Ошибка в запросе списка'),
            scan((acc, value) => acc.concat(value), [] as T[]),
        )),
    )

export const fromQuery = <T>(request: (query: Query) => Observable<TypedAjaxResponse<ListResponse<T>>>) =>
    (source$: Observable<string>): Observable<T[]> => source$.pipe(
        distinctUntilChanged(),
        switchMap(search => source$.pipe(
            startWith(search),
            paginateSearch(50),
            concatMap(request),
            listResponse('Ошибка в запросе списка'),
            scan((acc, value) => acc.concat(value), [] as T[]),
        )),
    )

export const innerListRequest = <T>(defaultError: string, requests: Observable<TypedAjaxResponse<RowResponse<T>>>[]): Observable<T[]> => forkJoin(requests).pipe(
    mergeMap(responses => isSuccessArray(responses) ?
        of(responses.map(res => res.response.row)) :
        throwError(responses.map(res => res.response).filter(isFailure).map(res => toastFailedResponse(res, defaultError)))
    ),
    defaultIfEmpty([] as T[]),
)